// Admin Layout with Sidebar

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import {
  LayoutDashboard,
  Package,
  Layers,
  Image as ImageIcon,
  FileText,
  Settings,
  Upload,
  LogOut,
  Menu,
  X,
  Users,
  KeyRound,
  MessageSquareQuote
} from 'lucide-react';

const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/dashboard/produtos', icon: Package, label: 'Produtos' },
    { path: '/dashboard/categorias', icon: Layers, label: 'Categorias' },
    { path: '/dashboard/banners', icon: ImageIcon, label: 'Banners' },
    { path: '/dashboard/paginas', icon: FileText, label: 'Editor do Site' },
    { path: '/dashboard/depoimentos', icon: MessageSquareQuote, label: 'Depoimentos' },
    { path: '/dashboard/midia', icon: ImageIcon, label: 'Biblioteca de Mídia' },
    { path: '/dashboard/clientes', icon: Users, label: 'Clientes / Leads' },
    { path: '/dashboard/importar', icon: Upload, label: 'Importar/Exportar' },
    { path: '/dashboard/senha', icon: KeyRound, label: 'Alterar Senha' },
    { path: '/dashboard/configuracoes', icon: Settings, label: 'Configurações' },
  ];

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();


  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    if (window.confirm('Tem certeza que deseja sair?')) {
      logout();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            
            <Link to="/dashboard" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_hydro-specialist/artifacts/1se2hklr_Verde%20e%20Branco%20Startup%20de%20Soluc%CC%A7o%CC%83es%20Agrono%CC%82micas%20Moderno%20Carta%CC%83o%20de%20Visita%20-%201.jpeg"
                alt="TerraHydro"
                className="h-10 w-auto"
              />
              <span className="hidden sm:block font-semibold text-gray-800">
                Painel Administrativo
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="hidden sm:flex"
            >
              Ver Site Público
            </Button>
            
            <div className="flex items-center gap-3 border-l border-gray-200 pl-4">
              <div className="hidden sm:block text-right">
                <div className="text-sm font-semibold text-gray-800">
                  {user?.full_name || user?.email}
                </div>
                <div className="text-xs text-gray-500">Administrador</div>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                <LogOut size={18} />
                <span className="hidden sm:inline ml-2">Sair</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 transition-transform duration-300 ease-in-out`}
        >
          <nav className="p-4 space-y-1 mt-16 lg:mt-0">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    active
                      ? 'bg-[#055509] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8">
          {children}
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default AdminLayout;
